import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Switch
} from '@mui/material';

import {
  Home,
  Briefcase,
  Star,
  LayoutList,
  GitCompare,
  Flame,
  SlidersHorizontal,
  Landmark,
  LayoutGrid,
  Bitcoin,
  Coins,
  BarChart2,
  Scale,
  Activity,
  Users,
  Crosshair,
  Eye,
  Shield,
  MessageSquare,
  CalendarDays,
  CalendarClock,
  CalendarCheck,
  CalendarRange,
  Calculator,
  LineChart,
  PiggyBank,
  Newspaper,
  Sparkles,
  User,
  LogOut,
  Moon,
  Sun,
  Menu as MenuIcon,
  ChevronDown
} from "lucide-react";

import SearchBar from './SearchBar';
import { NotificationBell } from './alerts';
import { useAuth } from './AuthContext';
import { useTheme } from '@mui/material/styles';
import { NotificationProvider } from '../contexts/NotificationContext';
import logo from '../images/rodeoLogo.png';

const iconProps = { size: 20, strokeWidth: 1.75 };

const AuthenticatedLayout = ({ children, darkMode, setDarkMode }) => {

  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const handleMenuOpen = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const theme = useTheme();

  const handleLogout = () => {
    logout()
      .then(() => {
        window.location.assign('/');
      })
      .catch(error => {
        console.error('Error logging out:', error);
      });
  };

  const upgradeButtonStyle = {
    background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '20px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'transform 0.2s, box-shadow 0.2s',
    animation: 'pulse 2s infinite',
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 25px rgba(255, 107, 107, 0.3)',
      background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
    },
  };

  return (
    <NotificationProvider>
      <div>

        <AppBar position="static">
          <Toolbar>

            <IconButton
              edge="start"
              aria-label="menu"
              onClick={handleDrawerOpen}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                borderRadius: 1,
                padding: '8px',
                marginRight: 1,
              }}
            >
              <MenuIcon {...iconProps} />
            </IconButton>

            <Box
              sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src={logo}
                alt="MarketRodeo"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '6px',
                  display: { xs: 'none', sm: 'block' }
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg,#00d4ff,#0099ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                MarketRodeo
              </Typography>

            </Box>

            <SearchBar />

            {user?.planName === 'Free' && (
              <Button
                component={Link}
                to="/pricing"
                sx={{ ...upgradeButtonStyle, ml: 2, display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Upgrade
              </Button>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationBell />

              <Button onClick={handleMenuOpen} sx={{ minWidth: 0, p: 0 }}>
                <User size={28} strokeWidth={1.75} color={theme.palette.secondary.main} />
              </Button>
            </Box>

          </Toolbar>
        </AppBar>

        <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>

          <Box sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <img src={logo} alt="MarketRodeo" style={{ width: 32, height: 32, borderRadius: '6px' }} />

            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg,#00d4ff,#0099ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              MarketRodeo
            </Typography>
          </Box>

          <List>

            {/* Home */}
            <ListItemButton component={Link} to="/" onClick={handleDrawerClose}>
              <ListItemIcon><Home {...iconProps} /></ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>

            {/* Portfolio */}
            <ListItemButton component={Link} to="/portfolio" onClick={handleDrawerClose}>
              <ListItemIcon><Briefcase {...iconProps} /></ListItemIcon>
              <ListItemText primary="Portfolio" />
            </ListItemButton>

            {/* Watchlist */}
            <ListItemButton component={Link} to="/watchlist" onClick={handleDrawerClose}>
              <ListItemIcon><Star {...iconProps} /></ListItemIcon>
              <ListItemText primary="Watchlist" />
            </ListItemButton>

            {/* Lists */}
            <ListItemButton component={Link} to="/lists" onClick={handleDrawerClose}>
              <ListItemIcon><LayoutList {...iconProps} /></ListItemIcon>
              <ListItemText primary="Lists" />
            </ListItemButton>

            {/* Asset Comparison */}
            <ListItemButton component={Link} to="/asset-comparison" onClick={handleDrawerClose}>
              <ListItemIcon><GitCompare {...iconProps} /></ListItemIcon>
              <ListItemText primary="Asset Comparison" />
            </ListItemButton>

            {/* Market Movers */}
            <ListItemButton component={Link} to="/market-movers" onClick={handleDrawerClose}>
              <ListItemIcon><Flame {...iconProps} /></ListItemIcon>
              <ListItemText primary="Market Movers" />
            </ListItemButton>

            {/* Screener */}
            <ListItemButton component={Link} to="/screener" onClick={handleDrawerClose}>
              <ListItemIcon><SlidersHorizontal {...iconProps} /></ListItemIcon>
              <ListItemText primary="Screener" />
            </ListItemButton>

            {/* Top Funds */}
            <ListItemButton component={Link} to="/hedge-funds" onClick={handleDrawerClose}>
              <ListItemIcon><Landmark {...iconProps} /></ListItemIcon>
              <ListItemText primary="Top Funds" />
            </ListItemButton>

            {/* Market Heatmap */}
            <ListItemButton component={Link} to="/heatmap" onClick={handleDrawerClose}>
              <ListItemIcon><LayoutGrid {...iconProps} /></ListItemIcon>
              <ListItemText primary="Market Heatmap" />
            </ListItemButton>

            {/* Crypto */}
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ChevronDown {...iconProps} />}>
                <ListItemIcon><Bitcoin {...iconProps} /></ListItemIcon>
                <ListItemText primary="Crypto" />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <ListItemButton component={Link} to="/crypto-quotes-screener">
                  <ListItemIcon><Coins {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Crypto Screener" />
                </ListItemButton>
                <ListItemButton component={Link} to="/crypto-avg-screener">
                  <ListItemIcon><BarChart2 {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Crypto Avg Screener" />
                </ListItemButton>
              </AccordionDetails>
            </Accordion>

            {/* Congress */}
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ChevronDown {...iconProps} />}>
                <ListItemIcon><Scale {...iconProps} /></ListItemIcon>
                <ListItemText primary="Congress" />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <ListItemButton component={Link} to="/congress-tracker">
                  <ListItemIcon><Activity {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Congress Tracker" />
                </ListItemButton>
                <ListItemButton component={Link} to="/congress-members">
                  <ListItemIcon><Users {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Congress Members" />
                </ListItemButton>
              </AccordionDetails>
            </Accordion>

            {/* Trackers */}
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ChevronDown {...iconProps} />}>
                <ListItemIcon><Crosshair {...iconProps} /></ListItemIcon>
                <ListItemText primary="Trackers" />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <ListItemButton component={Link} to="/insider-tracker">
                  <ListItemIcon><Eye {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Insider Trading Tracker" />
                </ListItemButton>
                <ListItemButton component={Link} to="/potus-tracker">
                  <ListItemIcon><Shield {...iconProps} /></ListItemIcon>
                  <ListItemText primary="POTUS Tracker" />
                </ListItemButton>
                <ListItemButton component={Link} to="/reddit-tracker">
                  <ListItemIcon><MessageSquare {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Reddit Tracker" />
                </ListItemButton>
              </AccordionDetails>
            </Accordion>

            {/* Calendars */}
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ChevronDown {...iconProps} />}>
                <ListItemIcon><CalendarDays {...iconProps} /></ListItemIcon>
                <ListItemText primary="Calendars" />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <ListItemButton component={Link} to="/earnings-calendar">
                  <ListItemIcon><CalendarClock {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Earnings Calendar" />
                </ListItemButton>
                <ListItemButton component={Link} to="/dividend-calendar">
                  <ListItemIcon><CalendarCheck {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Dividend Calendar" />
                </ListItemButton>
                <ListItemButton component={Link} to="/economic-calendar">
                  <ListItemIcon><CalendarRange {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Economic Calendar" />
                </ListItemButton>
              </AccordionDetails>
            </Accordion>

            {/* Calculators */}
            <Accordion sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ChevronDown {...iconProps} />}>
                <ListItemIcon><Calculator {...iconProps} /></ListItemIcon>
                <ListItemText primary="Calculators" />
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <ListItemButton component={Link} to="/stock-return-calculator">
                  <ListItemIcon><LineChart {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Stock Return Calculator" />
                </ListItemButton>
                <ListItemButton component={Link} to="/dividend-calculator">
                  <ListItemIcon><PiggyBank {...iconProps} /></ListItemIcon>
                  <ListItemText primary="Dividend Calculator" />
                </ListItemButton>
              </AccordionDetails>
            </Accordion>

            {/* News */}
            <ListItemButton component={Link} to="/News" onClick={handleDrawerClose}>
              <ListItemIcon><Newspaper {...iconProps} /></ListItemIcon>
              <ListItemText primary="News" />
            </ListItemButton>

            {/* RodeoAI */}
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Button
                component={Link}
                to="/chat"
                fullWidth
                variant="contained"
                startIcon={<Sparkles {...iconProps} />}
                sx={{
                  background: 'linear-gradient(135deg,#667eea,#764ba2)',
                  color: 'white',
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '15px',
                  fontWeight: 600
                }}
              >
                RodeoAI
              </Button>
            </Box>

          </List>

          <Divider />

        </Drawer>

        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>

          <MenuItem sx={{ justifyContent: "center" }}>
            <Switch
              checked={darkMode}
              onChange={() => setDarkMode(!darkMode)}
              icon={<Moon size={16} />}
              checkedIcon={<Sun size={16} />}
              sx={{ transform: "scale(1.2)" }}
            />
          </MenuItem>

          <Divider />

          <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
            <ListItemIcon><User {...iconProps} /></ListItemIcon>
            Profile
          </MenuItem>

          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogOut {...iconProps} /></ListItemIcon>
            Log out
          </MenuItem>

        </Menu>

        <main>{children}</main>

      </div>
    </NotificationProvider>
  );
};

export default AuthenticatedLayout;